/**
 * Shared loader for the report account/group picker options (`/codenames`).
 *
 * Fetched once and memoized (then re-read when stale, see utils/cacheFreshness) so:
 *  - the backend's market-value aggregate is warmed at login (preload), not
 *    lazily on the first report popup, and
 *  - the ~15 report popups share one payload instead of each re-fetching.
 *
 * Cleared on logout so a new user never sees the previous user's accounts.
 */
import { API_ENDPOINTS } from '../../../config/api';
import { STORAGE_KEYS } from '../../../config/constants';
import { refreshIfStale } from '../../../utils/cacheFreshness';
import { setLatestDataDate, setMaxSelectableDate } from './reportDates';

// Persist the payload's day and picker ceiling for the report date inputs. A
// known ceiling is never replaced with null: a degraded payload has none (see
// isDegraded below), and the day already ignores a missing value.
function persistDates(payload) {
  setLatestDataDate(payload && payload.latest_data_date);
  if (payload && payload.max_selectable_date) setMaxSelectableDate(payload.max_selectable_date);
}

// The login response bundles the /codenames payload and SignInPage stashes it in
// sessionStorage just before the post-login hard reload. Seed the cache from it
// once, on module load, so the Portfolios list + report pickers render with zero
// fetch. One-shot: the key is removed on read so it never outlives the session
// that minted it (and a later user on the same tab cannot read it). A missing or
// malformed payload simply yields no seed and the normal fetch path runs.
function seedFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.CODENAMES_PRELOAD);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEYS.CODENAMES_PRELOAD);
    const parsed = JSON.parse(raw);
    // Persist the authoritative "latest data date" and the picker ceiling
    // durably (the preload key is one-shot/removed) so report date defaults and
    // the date inputs' max can read them.
    persistDates(parsed);
    return parsed;
  } catch (_) {
    return null;
  }
}

// Stash the login-bundled /codenames payload so the next module load (after the
// post-login hard reload) seeds the cache from it via seedFromSession. SignInPage
// calls this just before its hard reload; pairing the write with the read keeps
// the storage key owned by a single module.
export function stashCodenamesPreload(codenames) {
  if (!codenames) return;
  try {
    sessionStorage.setItem(STORAGE_KEYS.CODENAMES_PRELOAD, JSON.stringify(codenames));
  } catch (_) {
    // sessionStorage full/unavailable - the list falls back to a fetch.
  }
}

let cache = seedFromSession(); // resolved /codenames payload (login-preloaded if present)
let cachedAt = Date.now(); // when `cache` was last read from the backend
let inflight = null; // dedupes concurrent callers

function authHeader() {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// The default fetcher: a raw fetch with the stored token (the login-time preload
// has a fresh one). The freshness watcher passes AuthContext's fetchWithAuth instead.
const rawFetch = (url) => fetch(url, { headers: authHeader() });

// A fail-soft /codenames payload: flagged by the backend, or with no day at all.
const isDegraded = (payload) => !payload || payload.degraded === true || !payload.latest_data_date;

/**
 * Resolve the picker options, reusing the cache (or an in-flight request).
 * Pass `{ force: true }` to bypass the cache and refetch. Pass `fetcher`
 * (AuthContext's fetchWithAuth) for 401 -> refresh -> retry; the default is the
 * raw fetch above. `force` and `fetcher` work the same on the two loaders below.
 */
export function loadPortfolioOptions({ force = false, fetcher } = {}) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  inflight = (fetcher || rawFetch)(API_ENDPOINTS.reports.getCodenames)
    .then((res) => {
      if (!res.ok) throw new Error(`codenames ${res.status}`);
      return res.json();
    })
    .then((data) => {
      // /codenames fails soft with a 200: any failed lake read ships a partial
      // payload flagged `degraded: true` (+ `degraded_parts`, names only), and a
      // failed day probe ships latest_data_date null. Neither may replace a good
      // cached payload (and drop its day, ceiling and values) - treat it as a
      // failed refresh: keep the old data and the old age, retry on the next
      // check. With no good payload yet (first load, or a degraded login bundle)
      // it is used as is.
      if (cache && !isDegraded(cache) && isDegraded(data)) {
        throw new Error(`codenames degraded: ${((data && data.degraded_parts) || ['latest_data_date']).join(',')}`);
      }
      cache = data;
      cachedAt = Date.now();
      inflight = null;
      persistDates(data);
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Warm the cache at login (fire-and-forget; errors are swallowed). */
export function preloadPortfolioOptions() {
  loadPortfolioOptions().catch(() => {});
}

let volumeCache = null; // resolved { account_number: volume } map (session-stable)
let volumeInflight = null;

/**
 * Resolve trailing-window gross trade volume per account, keyed by account_number.
 * NOT preloaded at login — fetched only when the Portfolios tab is sorted by
 * recent trade volume, then memoized for the session. Returns a Map for O(1)
 * lookups; resolves to an empty Map on failure so the caller degrades to
 * market-value ordering.
 */
export function loadTradeVolume() {
  if (volumeCache) return Promise.resolve(volumeCache);
  if (volumeInflight) return volumeInflight;
  volumeInflight = fetch(API_ENDPOINTS.reports.portfolioTradeVolume, { headers: authHeader() })
    .then((res) => {
      if (!res.ok) throw new Error(`trade-volume ${res.status}`);
      return res.json();
    })
    .then((data) => {
      volumeCache = new Map(Object.entries(data.volume_by_account || {}));
      volumeInflight = null;
      return volumeCache;
    })
    .catch((err) => {
      volumeInflight = null;
      throw err;
    });
  return volumeInflight;
}

// resolved Map(account_number -> { mv, prev_mv, net_flow, position_change,
// as_of, prev_as_of }) | null
let mvChangeCache = null;
let mvChangeAt = 0; // when mvChangeCache was last read from the backend
let mvChangeInflight = null;

/**
 * Resolve per-account latest + prior performance-day market value, net client
 * flow and position change, keyed by account_number. Fetched fresh by the
 * Portfolios tab (market value is the default view) so it shows real values and
 * a day-over-day change even when the login-preload market-value aggregate
 * timed out (cold lake -> zeros). Returns a Map for O(1) lookups; rejects on
 * failure - callers catch and degrade to the preload MV (no change line).
 */
export function loadMarketValueChange({ force = false, fetcher } = {}) {
  if (!force && mvChangeCache) return Promise.resolve(mvChangeCache);
  if (!force && mvChangeInflight) return mvChangeInflight;
  mvChangeInflight = (fetcher || rawFetch)(API_ENDPOINTS.reports.portfolioMarketValues)
    .then((res) => {
      if (!res.ok) throw new Error(`market-values ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const next = new Map(Object.entries(data.market_values || {}));
      // The endpoint fails soft too (a failed read is a 200 with an empty map):
      // do not replace a feed that has rows with it. A first load is used as is.
      if (mvChangeCache && mvChangeCache.size && !next.size) {
        throw new Error('market-values empty');
      }
      mvChangeCache = next;
      mvChangeAt = Date.now();
      mvChangeInflight = null;
      return mvChangeCache;
    })
    .catch((err) => {
      mvChangeInflight = null;
      throw err;
    });
  return mvChangeInflight;
}

/**
 * The freshness refresher for this module's two session caches (the picker
 * payload and the market-value change feed); see utils/cacheFreshness.
 */
export function refreshStalePortfolioOptions(fetcher) {
  refreshIfStale({ loaded: cache, busy: inflight, readAt: cachedAt, reload: loadPortfolioOptions }, fetcher);
  refreshIfStale(
    { loaded: mvChangeCache, busy: mvChangeInflight, readAt: mvChangeAt, reload: loadMarketValueChange },
    fetcher,
  );
}

/** Drop the cached options + any un-consumed preload key (call on logout / token clear). */
export function clearPortfolioOptions() {
  cache = null;
  inflight = null;
  volumeCache = null;
  volumeInflight = null;
  mvChangeCache = null;
  mvChangeInflight = null;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.CODENAMES_PRELOAD);
  } catch (_) {
    // sessionStorage unavailable - nothing to clear.
  }
}
